import React, { FC, useEffect, useMemo, useState } from 'react';
import classnames from 'classnames';
import { PositionLocationDropdown } from '@src/components/PositionLocationDropdown';
import { PositionLocationDropdownItem } from '@src/components/PositionLocationDropdown/interfaces/positionLocationDropdownItem.interface';
import './styles.scss';

interface MultipleLocationsProps {
  defaultLocations?: PositionLocationDropdownItem[];
  isInvalid?: boolean;
  limit: number;
  onLocationSelected: (locations: PositionLocationDropdownItem[]) => void;
  shouldClearOnSelect?: boolean;
}

const EMPTY_LOCATION_KEY = 'empty-field';

export const MultipleLocations: FC<MultipleLocationsProps> = ({
  defaultLocations,
  onLocationSelected,
  limit,
  isInvalid,
  shouldClearOnSelect,
}) => {
  const locationById = (locations: PositionLocationDropdownItem[]): Record<string, PositionLocationDropdownItem> => {
    return locations.reduce<Record<string, PositionLocationDropdownItem>>((acc, location) => {
      acc[location.id] = location;

      return acc;
    }, {});
  };

  const setDefaultLocations = (
    locations?: PositionLocationDropdownItem[],
  ): Record<string, PositionLocationDropdownItem | null> => {
    return locations?.length ? locationById(locations) : { [EMPTY_LOCATION_KEY]: null };
  };

  const [locationsById, setLocationsById] = useState(setDefaultLocations(defaultLocations));

  const locations = useMemo(() => {
    return Object.values(locationsById) || [];
  }, [locationsById]);

  const locationCount = locations.length;
  const isAddDisabled = locationCount >= limit || locationsById[EMPTY_LOCATION_KEY] === null;

  const filterLocations = (locations: (PositionLocationDropdownItem | null)[]): PositionLocationDropdownItem[] => {
    return locations.filter((location): location is PositionLocationDropdownItem => !!location);
  };

  const handleSelect = (location: PositionLocationDropdownItem): void => {
    const newLocations = { ...locationsById, [location.id]: location };

    delete newLocations[EMPTY_LOCATION_KEY];

    setLocationsById(newLocations);

    onLocationSelected(filterLocations(Object.values(newLocations)));
  };

  const handleRemove = (locationId: string): void => {
    const newLocations = { ...locationsById };

    delete newLocations[locationId];

    if (!Object.keys(newLocations).length) {
      newLocations[EMPTY_LOCATION_KEY] = null;
    }

    setLocationsById(newLocations);

    onLocationSelected(filterLocations(Object.values(newLocations)));
  };

  const addAnother = (): void => {
    setLocationsById({ ...locationsById, [EMPTY_LOCATION_KEY]: null });
  };

  useEffect(() => {
    setLocationsById(setDefaultLocations(defaultLocations));
  }, [defaultLocations?.map(location => location.id).join()]);

  return (
    <div className="multiple-locations">
      {locations.map((location, idx) => {
        return (
          <div className="multiple-locations-item" key={idx}>
            <PositionLocationDropdown
              isClearable
              shouldClearOnSelect={shouldClearOnSelect}
              isInvalid={isInvalid}
              onSelect={location => handleSelect(location)}
              placeholder="City"
              onClear={() => location?.id && handleRemove(location.id)}
              defaultValue={
                location
                  ? {
                      label: location.longName || location.shortName,
                      value: location.id,
                    }
                  : undefined
              }
            />
          </div>
        );
      })}

      <div
        className={classnames('multiple-locations-add-more', isAddDisabled && 'disabled')}
        onClick={() => !isAddDisabled && addAnother()}
      >
        Add another location
      </div>
    </div>
  );
};
